import { createAppSelector } from 'app-components/utils/core/reselect';
import type RootState from 'app-components/utils/redux-store/rootState';

import { type ACCOUNTS_REDUCER_KEY, accountsSelectors } from '../accounts';
import { getAccountById, isVehicleAccount } from '../accounts/accountsSelectors';
import { type FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from '../feature-flags';
import { type INSTITUTION_LOGINS_REDUCER_KEY, institutionLoginsSelectors } from '../institution-logins';
import {
  type KbbVehicleMake,
  type KbbVehicleModel,
  type KbbVehicleTrim,
  type MakeDTO,
  type ModelDTO,
  type TrimDTO,
  VEHICLE_REDUCER_KEY,
} from './vehicleTypes';

export type State = Pick<
  RootState,
  | typeof VEHICLE_REDUCER_KEY
  | typeof ACCOUNTS_REDUCER_KEY
  | typeof INSTITUTION_LOGINS_REDUCER_KEY
  | typeof FEATURE_FLAGS_REDUCER_KEY
>;

export const getVehicleStore = (state: RootState) => state[VEHICLE_REDUCER_KEY];

export const getKbbMakeOptions = (state: RootState) => getVehicleStore(state).makes;
export const getKbbModelOptions = (state: RootState) => getVehicleStore(state).models;
export const getKbbTrimOptions = (state: RootState) => getVehicleStore(state).trims;
export const getVehiclePreview = (state: RootState) => getVehicleStore(state).preview;

export const getKbbMakesLoading = (state: RootState) => getVehicleStore(state).loading.makes;
export const getKbbModelsLoading = (state: RootState) => getVehicleStore(state).loading.models;
export const getKbbTrimsLoading = (state: RootState) => getVehicleStore(state).loading.trims;

export const selectKbbMakeOptions = createAppSelector([getKbbMakeOptions], (makes: KbbVehicleMake[]): MakeDTO[] =>
  makes.map((make) => ({ makeId: make.makeId, makeName: make.makeName })),
);

export const selectKbbModelOptions = createAppSelector([getKbbModelOptions], (models: KbbVehicleModel[]): ModelDTO[] =>
  models.map((model) => ({ modelId: model.modelId, modelName: model.modelName })),
);

export const selectKbbTrimOptions = createAppSelector([getKbbTrimOptions], (trims: KbbVehicleTrim[]): TrimDTO[] =>
  trims.map((trim) => ({ trimId: trim.trimId, trimName: trim.trimName, modelYearId: trim.modelYearId })),
);

export const selectExistingVehicleAccount = createAppSelector(
  [(state: State, id?: string | null) => getAccountById(state, id)],
  (account) => (account && isVehicleAccount(account) ? account : null),
);

export const selectExistingVehicleAccountIsKbbConnected = (state: State, accountId: string | null | undefined) => {
  const institutionLoginId = selectExistingVehicleAccount(state, accountId)?.institutionLoginId;

  const institutionLogin = institutionLoginId
    ? institutionLoginsSelectors.getInstitutionLoginForId(state, institutionLoginId)
    : null;
  return institutionLogin?.channel === 'KELLEY_BLUE_BOOK';
};

export const selectKbbVehicleId = createAppSelector(
  [selectExistingVehicleAccount],
  (possibleExistingVehicleAccount) =>
    possibleExistingVehicleAccount?.aggregators?.find(
      (aggregator) => aggregator.cpId != undefined && aggregator.channel === 'KELLEY_BLUE_BOOK',
    )?.cpId,
);

export const selectKbbInfo = createAppSelector(
  [
    (state: State, accountId?: string | null) => selectExistingVehicleAccount(state, accountId),
    (state: State, accountId?: string | null) => selectExistingVehicleAccountIsKbbConnected(state, accountId),
    (state: State, accountId?: string | null) => selectKbbVehicleId(state, accountId),
  ],
  (possibleExistingVehicleAccount, isKbbConnected, kbbVehicleId) => ({
    possibleExistingVehicleAccount,
    isKbbConnected,
    kbbVehicleId,
  }),
);

export const getKelleyBlueBookFIId = createAppSelector(
  [institutionLoginsSelectors.getInstitutionLoginsById],
  (institutionLogins) => institutionLogins?.findKey((fi) => fi.get('channel') === 'KELLEY_BLUE_BOOK'),
);

export const getEnableKbbV2 = (state: State) => featureFlagsSelectors.getFeatureFlag(state, 'autoAssetV2');

export const selectShowKbbFlowOnEditAccount = createAppSelector(
  [getEnableKbbV2, selectExistingVehicleAccount],
  (autoAssetV2FeatureFlag, existingVehicleAccount) => autoAssetV2FeatureFlag && existingVehicleAccount,
);

export const selectIsDuplicateName = createAppSelector(
  [
    (state: State) => accountsSelectors.getVehicleAccounts(state),
    (state: State, accountName: string) => accountName,
    (state: State, _accountName, accountId: string) => accountId,
  ],
  (vehicleAccountsById, accountName, accountId) => {
    const vehicleAccounts = vehicleAccountsById.toList();
    return vehicleAccounts.some(
      (account) => account.id !== accountId && account.name?.toLowerCase() === accountName?.toLowerCase().trim(),
    );
  },
);
